import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Chip,
  IconButton, 
  Tooltip,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  AdminPanelSettings,
  AttachMoney,
  GitHub,
} from '@mui/icons-material';
import { API_BASE_URL, APP_CONFIG } from './config';
import { API_ENDPOINTS } from './api-config';
import { useAuth } from './contexts/AuthContext.jsx';
import { getAdminToken } from './utils/auth';

const fmtCurrency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

function Footer({ onAdminClick }) {
  const auth = useAuth() || {};
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentYear = new Date().getFullYear();
  const canViewFinancials = auth?.canViewFinancials?.() || false;
  const [platformCost, setPlatformCost] = useState(null);
  const hasPlatformCost = platformCost && platformCost.dataAvailable !== false;
  const platformCostLabel = hasPlatformCost
    ? `Platform ${fmtCurrency(platformCost.currentMonth)} this month`
    : 'Platform cost unavailable';
  const platformCostTooltip = hasPlatformCost
    ? `CloudCOR platform cost for ${platformCost.period || 'this month'} from exact synced provider billing data`
    : platformCost?.message || 'Exact CloudCOR resource billing data has not synced for this month';

  useEffect(() => {
    if (!canViewFinancials) {
      setPlatformCost(null);
      return undefined;
    }
    const controller = new AbortController();
    const token = getAdminToken();
    fetch(`${API_BASE_URL}${API_ENDPOINTS.costs.platform}`, {
      credentials: 'include',
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
      .then((resp) => resp.ok ? resp.json() : null)
      .then((payload) => {
        if (payload && !controller.signal.aborted) setPlatformCost(payload);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [canViewFinancials]);

  return (
    <Box 
      component="footer" 
      sx={{ 
        mt: 'auto',
        py: 4,
        px: 2,
        backgroundColor: theme.palette.mode === 'dark' 
          ? 'rgba(0, 0, 0, 0.1)' 
          : 'rgba(0, 0, 0, 0.02)',
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Container maxWidth="lg">
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2
          }}
        >
          {/* Copyright and Version */}
          <Box sx={{ textAlign: isMobile ? 'center' : 'left' }}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              © {currentYear} {APP_CONFIG.company || 'JSSeidel'} | All Rights Reserved.
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
              {APP_CONFIG.name} v{__APP_VERSION__}
            </Typography>
          </Box>

          {/* Social / Links */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
            {canViewFinancials && platformCost && (
              <Tooltip title={platformCostTooltip}>
                <Chip
                  size="small"
                  icon={<AttachMoney fontSize="small" />}
                  label={platformCostLabel}
                  variant="outlined"
                  color={hasPlatformCost ? 'default' : 'warning'}
                  sx={{ fontWeight: 700, color: hasPlatformCost ? 'text.secondary' : undefined }}
                />
              </Tooltip>
            )}
            <Tooltip title="Source Code">
              <IconButton 
                size="small" 
                color="inherit" 
                sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                component="a"
                href={APP_CONFIG.sourceRepoUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <GitHub fontSize="small" />
              </IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 0.5 }} />
            <Tooltip title="Admin Access">
              <IconButton 
                size="small" 
                color="primary"
                onClick={onAdminClick}
                sx={{ 
                  opacity: 0.6, 
                  '&:hover': { 
                    opacity: 1,
                    backgroundColor: theme.palette.primary.main + '10'
                  } 
                }}
              >
                <AdminPanelSettings fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
